import React, { useEffect, useRef } from 'react';
import { Check, Copy } from 'lucide-react';
import { createRoot } from 'react-dom/client';

export function HtmlRenderer({ html }: { html: string }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Auto-linkify plain text URLs
    const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s]|\bwww\.[^\s<]+[^<.,:;"')\]\s])/gi;
    const walk = document.createTreeWalker(containerRef.current, NodeFilter.SHOW_TEXT, null);
    const textNodes = [];
    let n;
    while ((n = walk.nextNode())) {
      textNodes.push(n);
    }

    textNodes.forEach((textNode) => {
      const parent = textNode.parentNode as HTMLElement;
      if (
        parent &&
        (parent.tagName === 'A' ||
          parent.tagName === 'PRE' ||
          parent.tagName === 'CODE' ||
          parent.closest('.premium-code-wrapper'))
      ) {
        return;
      }

      const text = textNode.nodeValue;
      if (text) {
        const matches = Array.from(text.matchAll(urlRegex));
        if (matches.length > 0) {
          const fragment = document.createDocumentFragment();
          let lastIndex = 0;
          for (const match of matches) {
            const url = match[0];
            const offset = match.index;
            if (offset !== undefined) {
              fragment.appendChild(document.createTextNode(text.slice(lastIndex, offset)));
              const a = document.createElement('a');
              a.href = url.startsWith('www.') ? 'https://' + url : url;
              a.textContent = url;
              a.target = '_blank';
              a.rel = 'noopener noreferrer';
              a.className =
                'text-blue-600 underline underline-offset-2 hover:text-blue-700 font-medium';
              a.style.cssText = 'color: #2563eb !important; text-decoration: underline !important;';
              fragment.appendChild(a);
              lastIndex = offset + url.length;
            }
          }
          fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
          textNode.parentNode?.replaceChild(fragment, textNode);
        }
      }
    });

    const aTags = containerRef.current.querySelectorAll('a');
    aTags.forEach((a) => {
      // Don't style anchor tags that might be inside a code block (though unlikely, good for safety)
      if (a.closest('.premium-code-wrapper')) return;

      a.className = 'text-blue-600 underline underline-offset-2 hover:text-blue-700 font-medium';
      a.style.cssText = 'color: #2563eb !important; text-decoration: underline !important;';
    });

    const preTags = containerRef.current.querySelectorAll('pre');

    preTags.forEach((pre) => {
      // If this pre is nested inside an already processed wrapper, just strip its styles and skip creating a double header.
      if (pre.closest('.premium-code-wrapper')) {
        pre.className = 'bg-transparent p-0 m-0 border-none rounded-none text-inherit shadow-none';
        pre.style.cssText =
          'background-color: transparent !important; border: none !important; margin: 0 !important; padding: 0 !important; box-shadow: none !important;';
        return;
      }

      // Avoid multiple initializations in React Strict Mode
      if (pre.dataset.processed === 'true') return;
      pre.dataset.processed = 'true';

      const code = pre.querySelector('code');
      const textToCopy = code ? code.innerText : pre.innerText;

      // Create outer wrapper
      const wrapper = document.createElement('div');
      wrapper.className =
        'premium-code-wrapper my-8 rounded-xl overflow-hidden shadow-2xl border border-slate-800 bg-[#0d1117] not-prose text-slate-300';

      // Inject force-styles to overcome Jodit's inline black colors
      const style = document.createElement('style');
      style.innerHTML = `
        .premium-code-wrapper pre,
        .premium-code-wrapper pre * {
          color: #e2e8f0 !important;
          background-color: transparent !important;
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
        }
      `;
      wrapper.appendChild(style);

      // Create Mac-style header
      const header = document.createElement('div');
      header.className =
        'flex items-center justify-between px-4 py-3 bg-[#161b22] border-b border-slate-800/50';

      const dots = document.createElement('div');
      dots.className = 'flex items-center gap-2';
      dots.innerHTML = `
        <div class="w-3 h-3 rounded-full bg-[#ff5f56] border border-[#e0443e]"></div>
        <div class="w-3 h-3 rounded-full bg-[#ffbd2e] border border-[#dea123]"></div>
        <div class="w-3 h-3 rounded-full bg-[#27c93f] border border-[#1aab29]"></div>
      `;

      const actionContainer = document.createElement('div');
      actionContainer.className = 'flex items-center';

      header.appendChild(dots);
      header.appendChild(actionContainer);

      // Wrap the pre tag
      pre.parentNode?.insertBefore(wrapper, pre);
      wrapper.appendChild(header);

      // Clean up pre tag styling to remove global css effects
      pre.className =
        'py-5 px-8 overflow-x-auto text-sm font-mono text-slate-300 leading-relaxed bg-transparent m-0 border-none rounded-none w-full';
      pre.style.cssText =
        'background-color: transparent !important; margin: 0 !important; border: none !important; border-radius: 0 !important; width: 100% !important;';

      if (code) {
        code.className =
          'bg-transparent p-0 border-none shadow-none text-inherit before:hidden after:hidden block w-full';
        code.style.cssText =
          'background-color: transparent !important; border: none !important; font-family: inherit !important; display: block; min-width: 100%;';
      }
      wrapper.appendChild(pre);

      const CopyButton = () => {
        const [copied, setCopied] = React.useState(false);
        return (
          <button
            onClick={() => {
              navigator.clipboard.writeText(textToCopy);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-sans font-medium text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            title="Copy code"
          >
            {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        );
      };

      const root = createRoot(actionContainer);
      root.render(<CopyButton />);
    });

    const tableTags = containerRef.current.querySelectorAll('table');
    tableTags.forEach((table) => {
      if (table.closest('.table-responsive-wrapper')) return;

      const wrapper = document.createElement('div');
      wrapper.className =
        'table-responsive-wrapper w-full overflow-x-auto my-6 border border-slate-200 rounded-lg shadow-sm';
      table.parentNode?.insertBefore(wrapper, table);

      table.className = 'min-w-full m-0 border-none';
      table.style.cssText = 'margin: 0 !important; border: none !important;';
      wrapper.appendChild(table);
    });
  }, [html]);

  return (
    <div
      ref={containerRef}
      className="prose prose-slate prose-lg prose-a:text-blue-600 prose-a:underline prose-a:underline-offset-2 hover:prose-a:text-blue-700 max-w-none jodit-content"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
